/**
 * Scrollable text-mode response panel. Mirrors the native
 * `CompanionTextResponseOverlay.swift`:
 *
 *   - 500 pt wide, 600 pt max height (grows as content streams in).
 *   - Header row with "KOKO" label + "esc" hint + 1 px divider.
 *   - Animated "..." loading dots until the first chunk arrives.
 *   - Scrollable once content exceeds max height.
 *   - Anchored to the bird with 60 fps tracking so it stays docked,
 *     but auto-flips to the opposite side when the bird is near the
 *     right edge.
 *   - Dismissable via Escape or outside click.
 *
 * Used when `settings.textOnlyMode` is on (no TTS) or when the
 * proactive observer fires an unprompted observation.
 */

const PANEL_MAX_WIDTH_PX = 500;
const GAP_FROM_BIRD_CENTER_PX = 26 + 12;
const POSITION_TICK_MS = 1000 / 60;
// Auto-hide after a duration that scales with text length so a one-line
// reply doesn't overstay its welcome but a multi-paragraph observation
// stays long enough to read. ~150 wpm reading speed ≈ 50 ms/char.
const AUTO_HIDE_BASE_MS = 4000;
const AUTO_HIDE_PER_CHARACTER_MS = 50;
const AUTO_HIDE_MAX_MS = 30_000;

export interface TextResponsePanelOptions {
  getBirdCenter: () => { x: number; y: number };
  onDismiss: () => void;
}

export class TextResponsePanel {
  private readonly bubbleElement: HTMLDivElement;
  private readonly fillLayer: HTMLDivElement;
  private readonly contentElement: HTMLDivElement;
  private readonly spinnerElement: HTMLSpanElement;
  private readonly options: TextResponsePanelOptions;
  private positionTimerId: number | null = null;
  private outsideClickListener: ((event: MouseEvent) => void) | null = null;
  private keyDownListener: ((event: KeyboardEvent) => void) | null = null;
  private spinnerIntervalId: number | null = null;
  private spinnerDotCount = 1;
  private autoHideTimerId: number | null = null;

  constructor(options: TextResponsePanelOptions) {
    this.options = options;
    this.bubbleElement = document.createElement("div");
    this.bubbleElement.className = "koko-pixel-box koko-text-response-panel";

    this.fillLayer = document.createElement("div");
    this.fillLayer.className = "koko-pixel-box-fill";

    const header = document.createElement("div");
    header.className = "koko-text-response-header";
    const label = document.createElement("span");
    label.className = "koko-label";
    label.textContent = "KOKO";
    const escHint = document.createElement("span");
    escHint.className = "koko-esc-hint";
    escHint.textContent = "esc";
    header.appendChild(label);
    header.appendChild(escHint);

    this.spinnerElement = document.createElement("span");
    this.spinnerElement.className = "koko-spinner";
    this.spinnerElement.textContent = ".";

    this.contentElement = document.createElement("div");
    this.contentElement.className = "koko-text-response-content";

    this.fillLayer.appendChild(header);
    this.fillLayer.appendChild(this.spinnerElement);
    this.fillLayer.appendChild(this.contentElement);
    this.bubbleElement.appendChild(this.fillLayer);
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.bubbleElement);
  }

  beginStreaming(): void {
    this.cancelAutoHide();
    this.contentElement.textContent = "";
    this.spinnerElement.style.display = "inline-block";
    this.startSpinnerAnimation();
    this.positionToBird();
    this.bubbleElement.classList.add("visible");
    this.startPositionTimer();
    this.installDismissListeners();
  }

  updateStreaming(text: string): void {
    if (text.length > 0 && this.spinnerElement.style.display !== "none") {
      this.spinnerElement.style.display = "none";
      this.stopSpinnerAnimation();
    }
    this.contentElement.textContent = text;
    // Keep the latest content in view as the stream grows past max height.
    this.fillLayer.scrollTop = this.fillLayer.scrollHeight;
  }

  finishStreaming(): void {
    const text = this.contentElement.textContent ?? "";
    if (text.trim().length === 0) {
      this.hide();
      return;
    }
    // Auto-hide after a reading-time-aware delay so the panel doesn't
    // sit forever on screen waiting for an Esc press. Esc / outside
    // click still dismiss it instantly via the existing listeners.
    const delay = Math.min(
      AUTO_HIDE_MAX_MS,
      AUTO_HIDE_BASE_MS + text.length * AUTO_HIDE_PER_CHARACTER_MS,
    );
    this.cancelAutoHide();
    this.autoHideTimerId = window.setTimeout(() => {
      this.autoHideTimerId = null;
      this.options.onDismiss();
    }, delay);
  }

  hide(): void {
    this.bubbleElement.classList.remove("visible");
    this.stopPositionTimer();
    this.removeDismissListeners();
    this.stopSpinnerAnimation();
    this.cancelAutoHide();
  }

  private cancelAutoHide(): void {
    if (this.autoHideTimerId !== null) {
      window.clearTimeout(this.autoHideTimerId);
      this.autoHideTimerId = null;
    }
  }

  isVisible(): boolean {
    return this.bubbleElement.classList.contains("visible");
  }

  destroy(): void {
    this.hide();
    this.bubbleElement.remove();
  }

  private startSpinnerAnimation(): void {
    if (this.spinnerIntervalId !== null) return;
    this.spinnerDotCount = 1;
    this.spinnerElement.textContent = ".";
    this.spinnerIntervalId = window.setInterval(() => {
      this.spinnerDotCount = (this.spinnerDotCount % 3) + 1;
      if (this.spinnerElement.style.display !== "none") {
        this.spinnerElement.textContent = ".".repeat(this.spinnerDotCount);
      }
    }, 400);
  }

  private stopSpinnerAnimation(): void {
    if (this.spinnerIntervalId !== null) {
      window.clearInterval(this.spinnerIntervalId);
      this.spinnerIntervalId = null;
    }
  }

  private startPositionTimer(): void {
    if (this.positionTimerId !== null) return;
    this.positionTimerId = window.setInterval(
      () => this.positionToBird(),
      POSITION_TICK_MS,
    );
  }

  private stopPositionTimer(): void {
    if (this.positionTimerId !== null) {
      window.clearInterval(this.positionTimerId);
      this.positionTimerId = null;
    }
  }

  private positionToBird(): void {
    const birdCenter = this.options.getBirdCenter();
    const panelWidth = this.bubbleElement.offsetWidth || PANEL_MAX_WIDTH_PX;
    const panelHeight = this.bubbleElement.offsetHeight || 160;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let leftEdge = birdCenter.x + GAP_FROM_BIRD_CENTER_PX;
    if (leftEdge + panelWidth > viewportWidth - 16) {
      leftEdge = birdCenter.x - GAP_FROM_BIRD_CENTER_PX - panelWidth;
    }
    leftEdge = Math.max(8, leftEdge);

    let topEdge = birdCenter.y - panelHeight / 2;
    topEdge = Math.max(8, Math.min(viewportHeight - panelHeight - 8, topEdge));

    this.bubbleElement.style.left = `${leftEdge}px`;
    this.bubbleElement.style.top = `${topEdge}px`;
  }

  private installDismissListeners(): void {
    if (this.outsideClickListener || this.keyDownListener) return;
    this.outsideClickListener = (event) => {
      const path = event.composedPath();
      if (!path.includes(this.bubbleElement)) {
        this.options.onDismiss();
      }
    };
    this.keyDownListener = (event) => {
      if (event.key === "Escape") this.options.onDismiss();
    };
    // Defer registration so the click that opened us doesn't immediately close us.
    window.setTimeout(() => {
      if (this.outsideClickListener) {
        document.addEventListener("mousedown", this.outsideClickListener, true);
      }
      if (this.keyDownListener) {
        document.addEventListener("keydown", this.keyDownListener);
      }
    }, 0);
  }

  private removeDismissListeners(): void {
    if (this.outsideClickListener) {
      document.removeEventListener("mousedown", this.outsideClickListener, true);
      this.outsideClickListener = null;
    }
    if (this.keyDownListener) {
      document.removeEventListener("keydown", this.keyDownListener);
      this.keyDownListener = null;
    }
  }
}
